import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import HeaderLogo from "../asset/horizontal-3.png";

const navLinks = [
  { label: "Home", href: "#" },
  { label: "Products", href: "#products" },
  { label: "About", href: "#about" },
  { label: "Inquiry", href: "#inquiry" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("Home");

  // Handle scroll and section detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20);

      // Get all sections
      const sections = navLinks.map(link => ({
        id: link.label,
        element: document.querySelector(link.href === "#" ? "body" : link.href)
      }));

      // Find the current section
      const currentSection = sections.reduce((acc, section) => {
        if (!section.element) return acc;
        
        const rect = section.element.getBoundingClientRect();
        const offset = 100; // Adjust this value to change when the section is considered active

        if (rect.top <= offset && rect.bottom > offset) {
          return section.id;
        }
        return acc;
      }, "Home");

      setActiveSection(currentSection);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500
        ${isScrolled 
          ? 'bg-purple900/95 backdrop-blur-md py-2'
          : 'bg-gradient-to-b from-purple900/80 to-transparent py-4'}`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo area */}
          <div className="flex items-center">
            <img 
              src={HeaderLogo} 
              alt="Header Logo" 
              className={`transition-all duration-500 ${
                isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'
              } w-auto object-contain`}
            />
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-12">
            {navLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`group relative font-radon text-lg lg:text-xl px-3 py-2 
                  transition-all duration-300 hover:transform hover:scale-105
                  ${item.label === activeSection
                    ? "text-secondary font-medium"
                    : "text-purple100 hover:text-secondary"}`}
              >
                {item.label}
                <span
                  className={`absolute left-0 -bottom-1 h-0.5 transition-all duration-300
                    ${item.label === activeSection
                      ? "bg-secondary w-full"
                      : "bg-secondary/80 w-0 group-hover:w-full"}`}
                ></span>
              </a>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              className="text-purple100 p-2 hover:text-secondary transition-colors duration-300"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                className="transition-transform duration-300 transform hover:scale-110"
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-gradient-to-b from-purple900 to-purple800 border-t border-purple700/30">
            <nav className="max-w-7xl mx-auto">
              {navLinks.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={`block font-radon text-xl py-4 px-8 transition-all duration-300
                    ${item.label === activeSection
                      ? "text-secondary font-medium bg-purple800/40"
                      : "text-purple100 hover:text-secondary hover:bg-purple800/20"}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}